import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import dayjs from 'dayjs';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { FetchArgs } from '../models/core';

export function combine(path: string, ...otherPaths: string[]): string {
    const sep = '/';
    let result = path;
    otherPaths.forEach((seg) => {
        if (seg.length) {
            if (result && !result.endsWith(sep) && !seg.startsWith(sep)) result += sep;
            result += seg;
        }
    });
    return result;
}

export const fetchArgsToQueryString = (args: FetchArgs): string => {
    let queryString = '';
    if (args.filter) {
        queryString += getFilterQueryString(args.filter);
    }
    if (args.sort?.field?.length) {
        queryString = appendQuery(queryString, `sort.field=${args.sort.field}&sort.type=${args.sort.dir || 'asc'}`);
    }
    queryString = appendQuery(queryString, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
    return queryString;
};

export const appendQuery = (path: string, ...args: string[]): string => {
    let result = path;
    if (path.indexOf('?') < 0) {
        if (path.endsWith('/')) result = `${path.substring(0, path.length - 1)}?`;
        else result += '?';
    } else if (!result.endsWith('&')) result += '&';
    return result + args.join('&');
};

export function arrayToQuery(key: string, vals: any[]): string {
    if (vals && vals.length) return vals.map((val) => `${key}=${encodeURIComponent(val)}`).join('&');
    return '';
}

export function getFilterQueryString(filter: CompositeFilterDescriptor): string {
    let queryString = '';
    for (const prop in filter) {
        const val = filter[prop];
        if (Array.isArray(val)) {
            queryString = appendQuery(queryString, arrayToQuery(`filter.${prop}`, val as Array<unknown>));
        } else if (val instanceof Date) {
            queryString = appendQuery(queryString, `filter.${prop}=${val.toDateString()}`);
        } else if (!isEmpty(val)) {
            queryString = appendQuery(queryString, `filter.${prop}=${val}`);
        }
    }
    return queryString;
}

export function getQueryString(filter: CompositeFilterDescriptor): string {
    let queryString = '';
    for (const prop in filter) {
        const val = filter[prop];
        if (Array.isArray(val)) {
            queryString = appendQuery(queryString, arrayToQuery(prop, val as Array<any>));
        } else if (val instanceof Date) {
            queryString = appendQuery(queryString, `${prop}=${formatDate(val)}`);
        } else if (!isEmpty(val)) {
            queryString = appendQuery(queryString, `${prop}=${val}`);
        }
    }
    return queryString;
}

export function formatDate(date: Date): string {
    const pad = (amt): string => {
        if (amt < 10) {
            return '0' + amt;
        }
        return amt;
    };

    return (
        date.getFullYear() +
        '-' +
        pad(date.getMonth() + 1) +
        '-' +
        pad(date.getDate()) +
        'T' +
        pad(date.getHours()) +
        ':' +
        pad(date.getMinutes()) +
        ':' +
        pad(date.getSeconds()) +
        '.' +
        (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5) +
        'Z'
    );
}

export function addFiltersToURL(args: any, url: string): string {
    if (args.filter) {
        for (const prop in args.filter) {
            const val = args.filter[prop];
            const propName = prop;

            if (Array.isArray(val)) {
                url = appendQuery(url, arrayToQuery(propName, val));
            } else if (Object.prototype.toString.call(val) === '[object Date]') {
                url = appendQuery(url, `${prop}=${dayjs(val).format('YYYY-MM-DD')}`);
            } else if (!isEmpty(val)) {
                url = appendQuery(url, `${propName}=${val}`);
            }
        }
    }
    if (args.sort && args.sort.field.length) {
        url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
    }
    return url;
}
